import type { QueryClient } from '@tanstack/svelte-query';
import { getQuery } from '#lib/query-utils/getQuery.js';
import { SERIES_GALLERY_STALE_TIME } from '#lib/websites/editions/constants/gallery.js';
import { SeriesGallerySort } from '#lib/websites/editions/constants/sorting.js';
import {
	GetEditionsGalleryDocument,
	type GetEditionsGalleryQueryVariables,
} from '#lib/websites/editions/queries/__generated__/getEditionsGallery.generated.js';
import { getAppliedFilters } from '$global/features/filter-drawer/utils/getAppliedFilters/getAppliedFilters';
import { getAppliedSort } from '$global/features/filter-drawer/utils/getAppliedSort/getAppliedSort';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import type { SeriesPageLoadEvent } from '$routes/editions/series/[slug]/types';

// Re-exported from the shared constants file so the two flavours of this
// loader page identically (rest-migration/feature-flags.md).
export {
	SERIES_GALLERY_LIMIT,
	SERIES_GALLERY_STALE_TIME,
} from '#lib/websites/editions/constants/gallery.js';

export const seriesPageLoadGql = async ({
	parent,
	data,
	url,
}: SeriesPageLoadEvent) => {
	const parentData = await parent();
	const queryClient: QueryClient = parentData.queryClient;
	const editions = data.editions;
	const filterSections = data.filterSections;
	const searchParams = url.searchParams;
	// The route's PageData is a union of the two server loads while the flag is
	// in place; this branch only ever runs behind the GraphQL one.
	const baseVariables =
		data.editionsVariables as GetEditionsGalleryQueryVariables;

	queryClient.setQueryData(
		getQueryKeys(GetEditionsGalleryDocument, baseVariables),
		{
			pageParams: [0],
			pages: [editions],
		}
	);

	const filter = getAppliedFilters({
		searchParams,
		filterSections,
	});

	const sort = getAppliedSort({
		searchParams,
		defaultSort: [SeriesGallerySort.NumberAsc],
	});

	const editionsVariables: GetEditionsGalleryQueryVariables = {
		...baseVariables,
		sort: [...sort, SeriesGallerySort.LetterAsc, SeriesGallerySort.CodeAsc],
		filter: {
			_and: [...(baseVariables.filter?._and || []), ...filter],
		},
	};

	queryClient.prefetchInfiniteQuery({
		...getQuery(GetEditionsGalleryDocument, editionsVariables),
		initialPageParam: 0,
		staleTime: SERIES_GALLERY_STALE_TIME,
	});

	return {
		...parentData,
		...data,
		editionsVariables,
	};
};
